import React, { ReactElement } from 'react'
import {
  IProduct,
  CheckoutItemData,
  ManagedPaymentDetails,
  IPaymentMethod,
  OpenBankingPaymentData,
  Payments
} from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { Currencies } from '../util/Currencies'
import { getMethod, getMethodLogoByTicker } from '../util/Managed'
import { SecondsToPaymentPeriod, shortAddress, trimZeros } from '../util/Util'
import { IconLinkButton } from './Button'
import LinkExternal from './LinkExternal'
import Tooltip from './Tooltip'
import Table from './Table'
import { formatExplorerUrl } from '../util/Chains'
import { ExternalLinkText } from '../views/Events'
import TimeText from './TimeText'
import { Link } from 'react-router-dom'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import { Section, SubTitle, SectionContent, SectionItem, SectionBreaker } from './Shared'
import { InlinePill } from './DashboardPrimitives'
import { openBankingStatusFromProvider, openBankingStatusPresentation } from '../util/OpenBankingPayment'

const openBankingSenderName = (data?: OpenBankingPaymentData): string | undefined =>
  data?.senderName ??
  data?.payerFinancialAddress?.iban?.accountHolderName ??
  data?.payerFinancialAddress?.sortCode?.accountHolderName ??
  data?.payerFinancialAddress?.bankCode?.accountHolderName ??
  data?.payerFinancialAddress?.bban?.accountHolderName

const openBankingSenderAccountNumber = (data?: OpenBankingPaymentData): string | undefined =>
  data?.senderAccountNumber ??
  data?.payerFinancialAddress?.sortCode?.accountNumber ??
  data?.payerFinancialAddress?.bankCode?.accountNumber

type OpenBankingDetails = NonNullable<Payments['openBanking']>

const OpenBankingPaymentDetailsContent = ({
  payment,
  short
}: {
  payment: OpenBankingDetails
  short?: boolean
}): ReactElement => {
  const openBankingData = payment.openBankingPaymentData
  const providerStatus = openBankingData?.status ?? payment.status
  // One status in the same words as everywhere else a bank payment is shown.
  const statusHistory = openBankingData?.statusHistoryList ?? []
  const bankPaymentStatus = providerStatus
    ? openBankingStatusPresentation(openBankingStatusFromProvider(
      providerStatus,
      statusHistory[statusHistory.length - 1]?.reason,
      false
    ))
    : undefined
  const senderName = openBankingSenderName(openBankingData)
  const senderIban = openBankingData?.senderIban ?? openBankingData?.payerFinancialAddress?.iban?.iban
  const senderBic = openBankingData?.senderBic ??
    openBankingData?.payerFinancialAddress?.iban?.bic ??
    openBankingData?.payerFinancialAddress?.bban?.bic
  const senderSortCode = openBankingData?.senderSortCode ??
    openBankingData?.payerFinancialAddress?.sortCode?.sortCode
  const senderAccountNumber = openBankingSenderAccountNumber(openBankingData)
  const senderBankCode = openBankingData?.senderBankCode ??
    openBankingData?.payerFinancialAddress?.bankCode?.code
  const senderBban = openBankingData?.senderBban ?? openBankingData?.payerFinancialAddress?.bban?.bban

  return <>
    {
      short &&
      payment.paymentEventId &&
      <SectionItem>
        <SubTitle>ID</SubTitle>
        <ExternalLinkText href={`/payment/${payment.paymentEventId}`} target='_blank'>
          {payment.paymentEventId}
          <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
        </ExternalLinkText>
      </SectionItem>
    }

    <SectionContent>
      <SectionItem>
        <SubTitle>Asset</SubTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <PaymentMethodDisplay
            style={{ marginRight: 5 }}
            method={{ network: payment.network, token: payment.token }}
            excludeName
            disableTooltip={false} />
          {
            payment.token
          }
        </div>
      </SectionItem>

      <SectionItem>
        <SubTitle>
          <span>Total payment</span>
          <Tooltip message='The total payment amount expected to be paid by the contact.' />
        </SubTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <PaymentAssetIcon payment={payment} />
          <span>{Number(payment.amount)}</span>
        </div>
      </SectionItem>

      {
        bankPaymentStatus &&
        <SectionItem>
          <SubTitle>
            <span>Bank payment status</span>
            <Tooltip message={bankPaymentStatus.description} />
          </SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <InlinePill $tone={bankPaymentStatus.tone} style={{ fontSize: 12, padding: '6px 10px' }}>
              {bankPaymentStatus.label}
            </InlinePill>
          </div>
        </SectionItem>
      }

      {
        openBankingData?.bankName &&
        <SectionItem>
          <SubTitle>Bank</SubTitle>
          <div>{openBankingData.bankName}</div>
        </SectionItem>
      }
      {
        openBankingData?.paymentScheme &&
        <SectionItem>
          <SubTitle>Payment scheme</SubTitle>
          <div>{openBankingData.paymentScheme}</div>
        </SectionItem>
      }
      {
        openBankingData?.instantPaymentScheme !== undefined &&
        <SectionItem>
          <SubTitle>Instant rail</SubTitle>
          <div>{openBankingData.instantPaymentScheme ? 'Yes' : 'No'}</div>
        </SectionItem>
      }
      {
        senderName &&
        <SectionItem>
          <SubTitle>Sender name</SubTitle>
          <div>{senderName}</div>
        </SectionItem>
      }
      {
        senderIban &&
        <SectionItem>
          <SubTitle>Sender IBAN</SubTitle>
          <div>{senderIban}</div>
        </SectionItem>
      }
      {
        senderBic &&
        <SectionItem>
          <SubTitle>Sender BIC</SubTitle>
          <div>{senderBic}</div>
        </SectionItem>
      }
      {
        senderSortCode &&
        <SectionItem>
          <SubTitle>Sender sort code</SubTitle>
          <div>{senderSortCode}</div>
        </SectionItem>
      }
      {
        senderAccountNumber &&
        <SectionItem>
          <SubTitle>Sender account number</SubTitle>
          <div>{senderAccountNumber}</div>
        </SectionItem>
      }
      {
        senderBankCode &&
        <SectionItem>
          <SubTitle>Sender bank code</SubTitle>
          <div>{senderBankCode}</div>
        </SectionItem>
      }
      {
        senderBban &&
        <SectionItem>
          <SubTitle>Sender BBAN</SubTitle>
          <div>{senderBban}</div>
        </SectionItem>
      }
    </SectionContent>
  </>
}

export const ProductThumbnail = ({ p, disableLink = false }: { p: IProduct, disableLink?: boolean }): ReactElement => {
  const details = <>
    <div style={{
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }}>
      {p.imageUrl && <img src={p.imageUrl} style={{ width: 20, borderRadius: 5, height: 'auto' }} /> }
      {p.name}
    </div>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      fontSize: 14,
      columnGap: 5
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {Currencies.find(c => c.ticker.toLowerCase() === p.currency.toLowerCase())?.icon}
        <span>{new Intl.NumberFormat('en-US', { maximumFractionDigits: 12 }).format(p.price)}</span>
      </div>
      {
        p.chargingIntervalSeconds !== undefined && p.chargingIntervalSeconds > 0 &&
        <span style={{ fontSize: 12, color: RADOM_COLORS.GRAY_DARKER }}>
          per {SecondsToPaymentPeriod(p.chargingIntervalSeconds).toLowerCase()}
        </span>
      }
    </div>
  </>

  if (disableLink) {
    return <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      color: 'var(--ink, #20242d)',
      width: 'fit-content'
    }}>
      {details}
    </div>
  }

  return <IconLinkButton
    to={`/products/${p.id}`}
    target='_blank'
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      textDecoration: 'none',
      color: 'var(--ink, #20242d)',
      width: 'fit-content',
      padding: 0
    }}>
    {details}
    <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
  </IconLinkButton>
}

export const ItemDataThumbnail = ({ p }: { p: CheckoutItemData }): ReactElement => {
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    textDecoration: 'none',
    color: 'var(--ink, #20242d)',
    width: 'fit-content'
  }}>
    <div style={{
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }}>
      {p.imageUrl && <img src={p.imageUrl} style={{ width: 20, borderRadius: 5, height: 'auto' }} /> }
      {p.name}
    </div>
    <div style={{
      display: 'flex',
      fontSize: 14,
      columnGap: 5
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {Currencies.find(c => c.ticker.toLowerCase() === p.currency.toLowerCase())?.icon}
        <span>{new Intl.NumberFormat('en-US', { maximumFractionDigits: 12 }).format(Number(p.price))}</span>
      </div>
      {
        p.chargingIntervalSeconds !== undefined && p.chargingIntervalSeconds > 0 &&
      <span style={{ fontSize: 12, color: RADOM_COLORS.GRAY_DARKER }}>
        per {SecondsToPaymentPeriod(p.chargingIntervalSeconds).toLowerCase()}
      </span>
      }
    </div>

  </div>
}

export const PaymentNetwork = ({ payment }: { payment: ManagedPaymentDetails }): ReactElement => {
  const paymentMethod = getMethod(payment.network, payment.token)

  return <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
    <img src={paymentMethod.hostChain?.logo ?? paymentMethod.logo} style={{ height: 20, width: 'auto' }} />
    <span>
      { paymentMethod.hostChain?.name ?? paymentMethod.name }
    </span>
  </div>
}

export const PaymentAsset = ({ payment }: { payment: ManagedPaymentDetails }): ReactElement => {
  const paymentMethod = getMethod(payment.network, payment.token)

  return <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
    <img src={paymentMethod.logo} style={{ height: 20, width: 'auto' }} />
    <span>
      { paymentMethod.currencyName ?? paymentMethod.name }
    </span>
  </div>
}

export const PaymentAssetIcon = ({ payment }: { payment: IPaymentMethod }): ReactElement => {
  const paymentMethod = getMethod(payment.network, payment.token)
  return <img src={paymentMethod.logo} style={{ height: 15, width: 'auto' }} />
}

export const PaymentDetailsSection = ({ payment, netPayment, short }: {
  payment: Payments
  netPayment: number
  short?: boolean
}): ReactElement =>
  <Section>
    {
      !short &&
      <SubTitle style={{ fontSize: 18 }}>
        Payment details
      </SubTitle>
    }
    {
      payment.managed && <>
        <SectionContent>
          {
            short &&
        payment.managed.paymentEventId &&
        <SectionItem>
          <SubTitle>Id</SubTitle>
          <Link to={`/payment/${payment?.managed.paymentEventId}`} style={{ fontSize: 14, color: RADOM_COLORS.BLUE_MED }}>
            <span>{payment.managed.paymentEventId}</span>
          </Link>
        </SectionItem>
          }

          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <PaymentMethodDisplay
                style={{ marginRight: 5 }}
                method={{ network: payment.managed.network, token: payment.managed.token }}
                includeNetworkName
                disableTooltip={false} />
            </div>
          </SectionItem>
          <SectionItem>
            <SubTitle>
              <span>Gross payment</span>
              <Tooltip message='The total payment amount expected to be paid by the contact.' />
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <PaymentAssetIcon payment={payment.managed} />
              <span>{Number(payment.managed.amount)}</span>
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>
              <span>Net payment</span>
              <Tooltip message='The amount you receive after fee deduction.' />
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <PaymentAssetIcon payment={payment.managed} />
              <span>{netPayment}</span>
            </div>
          </SectionItem>

          {
            payment.managed.networkFee &&
        <SectionItem>
          <SubTitle>Network fee</SubTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <PaymentAssetIcon payment={payment.managed} />
            <span>{trimZeros(payment.managed.networkFee)}</span>
          </div>
        </SectionItem>
          }

          <SectionItem>
            <SubTitle>Radom fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <PaymentAssetIcon payment={payment.managed} />
              <span>{
                Number(payment.managed.amount) <= Number(payment.managed.networkFee ?? 0)
                  ? 0
                  : new Intl.NumberFormat('en-US', { maximumSignificantDigits: 8 }).format(
                    Number(payment.managed.amount) - netPayment - Number(payment.managed.networkFee ?? 0)
                  )
              }</span>
            </div>
          </SectionItem>

        </SectionContent>

        {
          !short &&
      <SectionBreaker />
        }

        {
          !short &&
      payment.managed.conversionRates.length > 0 &&
          <div>
            <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Conversion rates</SubTitle>
            {
              payment.managed.conversionRates.map((d, i) =>
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span>1</span>
                    {
                      getMethodLogoByTicker(d.from) &&
                      <img src={getMethodLogoByTicker(d.from)} style={{ width: 15, height: 'auto' }} />
                    }
                    {d.from}
                  </div>
                  <div>=</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span>{Number(d.rate)}</span>
                    {
                      getMethodLogoByTicker(d.to) &&
                      <img src={getMethodLogoByTicker(d.to)} style={{ width: 15, height: 'auto' }} />
                    }
                    {d.to}
                  </div>
                </div>
              )}

          </div>
        }

        {
          payment.managed.transactions.length > 0 &&
          <div>
            <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Payment transactions</SubTitle>
            <Table
              disableHoverStyle
              headers={[
                <span key="key">Transaction hash</span>,
                <span key="network">Network</span>,
                <span key="amount">Asset</span>,
                <span key="date">Date</span>
              ]}
              items={
                payment.managed.transactions.map(d => [
                  <div key="1">
                    <ExternalLinkText
                      onClick={e => e.stopPropagation()}
                      target="_blank" rel="noreferrer"
                      href={formatExplorerUrl(
                        d.network,
                        'tx',
                        d.transactionHash ?? ''
                      )}>
                      {shortAddress(d.transactionHash ?? '', 18)}
                      <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
                    </ExternalLinkText>
                  </div>,
                  <div key="4" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <PaymentNetwork payment={{ network: d.network, token: d.token } as any} />
                  </div>,
                  <div key="2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {
                      payment.managed &&
                      <PaymentAssetIcon payment={payment.managed} />
                    }
                    <span>{trimZeros(d.amount)}</span>
                  </div>,
                  <TimeText key="3" date={new Date(d.blockTimestamp)} />
                ])
              }
              isLoading={false}
            />
          </div>
        }</>
    }

    {
      payment.openBanking &&
      <OpenBankingPaymentDetailsContent payment={payment.openBanking} short={short} />
    }
  </Section>